import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scissors, Images, Calendar, User, Star, MapPin, ArrowUpRight } from "lucide-react";
import { trackEvent } from "@/hooks/useBusinessData";

const DESTINATIONS = [
  { key: "services", to: "/services", icon: Scissors, title: "Services",  copy: "Cuts, beards, color, twists & the ritual." },
  { key: "photos",   to: "/photos",   icon: Images,   title: "Photos",    copy: "The living portfolio — real work only." },
  { key: "book",     to: "/book",     icon: Calendar, title: "Book",      copy: "Reserve the chair with Calendly.",   featured: true },
  { key: "founder",  to: "/founder",  icon: User,     title: "Founder",   copy: "Yolanda — Long Island roots, ATL craft." },
  { key: "reviews",  to: "/reviews",  icon: Star,     title: "Reviews",   copy: "A decade of five-star Yelp loyalty." },
  { key: "visit",    to: "/visit",    icon: MapPin,   title: "Visit",     copy: "Midtown Atlanta · hours & directions." },
];

export default function DestinationPills() {
  return (
    <section
      id="destinations"
      className="relative bg-black text-white pt-12 pb-16 md:pt-20 md:pb-24 border-t border-white/10"
      data-testid="destination-pills"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-8 md:mb-12 items-end">
          <div className="col-span-12 md:col-span-7">
            <div className="eyebrow mb-4">§ Explore</div>
            <h2 className="font-serif text-2xl sm:text-4xl md:text-5xl leading-[1.02] tracking-tight">
              Precision cuts. Personal service.
              <br />
              <span className="italic text-zinc-400">
                A calm, elevated experience in Midtown Atlanta.
              </span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-8">
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-md">
              Choose your path — every experience has its own room to breathe.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 md:gap-4">
          {DESTINATIONS.map((d, i) => {
            const Icon = d.icon;
            return (
              <motion.div
                key={d.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.7, 0, 0.2, 1] }}
              >
                <Link
                  to={d.to}
                  onClick={() => trackEvent("destination_clicked", { destination: d.key })}
                  data-testid={`home-destination-${d.key}`}
                  className={`group block relative overflow-hidden border transition-colors duration-500 h-full min-h-[130px] md:min-h-[170px] p-4 md:p-7 ${
                    d.featured
                      ? "bg-white text-black border-white hover:bg-black hover:text-white"
                      : "bg-transparent text-white border-white/15 hover:bg-white hover:text-black"
                  }`}
                >
                  <div className="flex items-start justify-between h-full">
                    <div className="flex flex-col justify-between h-full min-h-[100px] md:min-h-[130px]">
                      <div className="flex items-center gap-2 md:gap-3">
                        <div
                          className={`w-8 h-8 md:w-10 md:h-10 rounded-full border flex items-center justify-center transition-colors ${
                            d.featured
                              ? "border-black/25 group-hover:border-white/40"
                              : "border-white/25 group-hover:border-black/40"
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" strokeWidth={1.4} />
                        </div>
                        <div className="font-mono uppercase tracking-[0.2em] text-[9px] md:text-[10px] opacity-60">
                          0{i + 1}
                        </div>
                      </div>
                      <div>
                        <div className="font-serif text-xl md:text-2xl lg:text-3xl leading-none">
                          {d.title}
                        </div>
                        <div className="text-xs md:text-sm mt-1.5 md:mt-2 opacity-70 leading-snug max-w-[220px]">
                          {d.copy}
                        </div>
                      </div>
                    </div>
                    <ArrowUpRight
                      className="w-4 h-4 md:w-5 md:h-5 flex-none transition-transform group-hover:-rotate-45"
                      strokeWidth={1.3}
                    />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
